import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { NOTICE_MODELS } from './models';
import { useRun } from './categoryRun';
// Side-effect import: configures the shared PDF.js worker (used here and by the
// off-screen categorize run).
import { loadPdf } from './pdfRender';
import { modalBackdropCls, modalCardCls, modalCloseCls, wideOverlayCls } from './overlay';

// A filtered view: restrict navigation to `pages` and, for a page only partly in
// the wanted category, black out the rest (keep only the `bands[page]` vertical
// span, 0..1 from the top). Section-boundary lines are hidden in this mode.
export type ViewerFilter = {
  pages: number[];
  bands: Record<number, { top: number; bottom: number }>;
};

// A part's box to draw over the page it was found on: its page, its normalized
// [x0,y0,x1,y1] (0..1 from the page's top-left) and the name to label it with.
// `firstBox` is the bulk pass's box (the first inference) when a later pass moved the
// box — kept so the viewer can toggle between the two.
export type PartBox = {
  page: number;
  bbox: [number, number, number, number];
  firstBox?: [number, number, number, number];
  label: string;
};

// Which of a part's boxes the page shows — the second (current) pass, the first, or
// nothing at all. One button cycles them in this order, and each state's colour, dot and
// label live together here so a state cannot be added with half its appearance missing.
type BoxPass = 'second' | 'first' | 'off';

const NEXT_BOX_PASS: Record<BoxPass, BoxPass> = {
  second: 'first',
  first: 'off',
  off: 'second',
};

const BOX_PASS_STYLE: Record<BoxPass, { button: string; dot: string; label: string }> = {
  second: {
    button:
      'border-blue-400 bg-blue-50 text-blue-700',
    dot: 'bg-blue-500',
    label: 'notice.parts.pass.second',
  },
  first: {
    button:
      'border-amber-400 bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
    label: 'notice.parts.pass.first',
  },
  off: {
    button:
      'border-slate-300 bg-white/80 text-slate-500',
    // Hollow: nothing is drawn, so nothing is filled in.
    dot: 'border border-slate-400',
    label: 'notice.parts.pass.off',
  },
};

// Either side of the zoomed page: the plain margin, or room for that side's section labels.
const ZOOM_GUTTER = 48;
const ZOOM_LABEL_GUTTER = 260;
// The overlay's padding (2 × 16) and the card's border (2 × 1): what the window's width
// loses before the card's gutters.
const ZOOM_FRAME_W = 34;

// Renders a PDF one page at a time onto a canvas, filling the container width
// (margins come from the narrow section the parent places this in). The header
// holds the page navigation. With `filter`, only the matching pages are shown
// (the rest of a partial page blacked out) and no boundary lines are drawn.
export function PdfViewer({
  file,
  docId,
  onNumPages,
  filter,
  partBoxes,
}: {
  file: Blob;
  docId: string;
  onNumPages?: (n: number) => void;
  filter?: ViewerFilter;
  // Part bounding boxes to draw over the pages they were found on (Phase 2).
  partBoxes?: PartBox[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const loadRef = useRef<PDFDocumentLoadingTask | null>(null);
  const taskRef = useRef<RenderTask | null>(null);
  const zoomCanvasRef = useRef<HTMLCanvasElement>(null);
  const zoomTaskRef = useRef<RenderTask | null>(null);
  const { t } = useLanguage();
  // Section segments detected by the categorize run, to overlay on the page.
  const { segments } = useRun(docId);

  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  // Which part boxes are drawn over the page, cycled by one button:
  //   'second' — the current boxes, refined by the second pass (emerald);
  //   'first'  — the bulk extraction's original boxes (amber), so the two can be compared;
  //   'off'    — none at all.
  // The third value is not a third opinion about the boxes: a parts page is a dozen small
  // drawings, and a dozen boxes with a name tag over each one cover the very artwork they
  // are pointing at. Off is how you look at the page itself.
  const [boxPass, setBoxPass] = useState<BoxPass>('second');
  const showFirstPass = boxPass === 'first';

  // Load (and reload on file change) the document.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNumPages(0);
    setPage(1);

    (async () => {
      try {
        const buf = await file.arrayBuffer();
        if (cancelled) return;
        const loadingTask = loadPdf(buf);
        const doc = await loadingTask.promise;
        if (cancelled) {
          void loadingTask.destroy();
          return;
        }
        loadRef.current = loadingTask;
        docRef.current = doc;
        setNumPages(doc.numPages);
      } catch {
        if (!cancelled) setError(t('notice.pdf.openError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      void loadRef.current?.destroy();
      loadRef.current = null;
      docRef.current = null;
    };
  }, [file]);

  // Track the available width and height so a page fits the viewport (and
  // re-renders crisply on resize).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // THE SAME SIZE IS NOT A NEW SIZE. This stored a fresh object on every reading, so an
    // observer that fired without anything having changed still replaced the state, still
    // re-rendered, and still re-rendered the page onto the canvas — and re-rendering the
    // canvas is a thing that can move the layout, which fires the observer again.
    //
    // A ResizeObserver fires on sub-pixel changes, and a page tall enough to bring the
    // window's scrollbar in and out oscillates by exactly that much. So the loop needed no
    // bug of its own: it needed the reader to get long, which is what writing every gesture
    // out did to it. Compared by value, a reading that says nothing changes nothing.
    const fit = (w: number, h: number) =>
      setSize((was) => (was.w === w && was.h === h ? was : { w, h }));
    fit(el.clientWidth, el.clientHeight);
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) fit(r.width, r.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Render the current page whenever it, the document, or the size changes.
  useEffect(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas || numPages === 0 || size.w === 0 || size.h === 0) return;
    let cancelled = false;

    (async () => {
      try {
        const pdfPage = await doc.getPage(page);
        if (cancelled) return;
        const base = pdfPage.getViewport({ scale: 1 });
        // Fit the whole page inside the container (contain), so it never needs
        // scrolling: limited by width or height, whichever is tighter, with a
        // little breathing room. The container is wide, so a portrait page is
        // height-limited and leaves black side bands for the labels.
        const scale = Math.min(size.w / base.width, size.h / base.height) * 0.96;
        const viewport = pdfPage.getViewport({ scale });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        taskRef.current?.cancel();
        const task = pdfPage.render({ canvas, canvasContext: ctx, viewport });
        taskRef.current = task;
        await task.promise;
      } catch {
        // RenderingCancelledException fires when pages switch quickly — ignore.
      }
    })();

    return () => {
      cancelled = true;
      taskRef.current?.cancel();
    };
  }, [page, numPages, size.w, size.h]);

  // Navigation: through the whole document, or — in filter mode — only through
  // the matching pages, stepping by index within that list.
  const go = useCallback(
    (delta: number) =>
      setPage((p) => {
        const nav = filter?.pages;
        if (nav && nav.length) {
          const i = nav.indexOf(p);
          const ni = Math.min(nav.length - 1, Math.max(0, (i < 0 ? 0 : i) + delta));
          return nav[ni];
        }
        return Math.min(numPages, Math.max(1, p + delta));
      }),
    [numPages, filter],
  );

  // Arrow keys page through the document; Escape closes the zoom modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'Escape') setZoomed(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  // In filter mode, keep the page on a matching one (snap to the first match
  // when the current page falls outside the set, e.g. on load or run change).
  useEffect(() => {
    const nav = filter?.pages;
    if (nav && nav.length && numPages > 0 && !nav.includes(page)) setPage(nav[0]);
  }, [filter, numPages, page]);

  // Report the page count.
  useEffect(() => {
    onNumPages?.(numPages);
  }, [numPages, onNumPages]);

  // First / last segment category of a page for a model (the categories carried
  // in at the top and out at the bottom of that page).
  const firstCat = (modelId: string, p: number) => segments[modelId]?.[p]?.[0]?.category;
  const lastCat = (modelId: string, p: number) => {
    const s = segments[modelId]?.[p];
    return s && s.length ? s[s.length - 1].category : undefined;
  };

  // Dashed section-boundary lines to draw on the current page, per model. A page
  // is a list of segments; each boundary BETWEEN two segments gets a mid-page
  // line, and the page's top/bottom edges get a line when a section starts/ends
  // there. A page-break boundary draws "(début)" at the top of this page (the
  // matching "(fin)" sits at the bottom of the previous page). Models are placed
  // on opposite sides so labels stay legible.
  const fin = t('notice.cat.sectionEnd');
  const debut = t('notice.cat.sectionStart');
  const enCours = t('notice.cat.sectionOngoing');
  const boundaryLines = NOTICE_MODELS.flatMap((m, i) => {
    const segs = segments[m.id]?.[page];
    if (!segs || segs.length === 0) return [];
    const side = i === 0 ? 'left' : 'right';
    const out: { key: string; y: number; color: string; side: string; above?: string; below?: string }[] = [];
    const top = segs[0].category;
    const bottom = segs[segs.length - 1].category;
    const prev = page > 1 ? lastCat(m.id, page - 1) : undefined;
    const next = page < numPages ? firstCat(m.id, page + 1) : undefined;
    // A page wholly inside one section: a single segment carried in from the
    // previous page and out to the next. Only such a page is "en cours"; pages
    // holding a start or an end are not.
    const interior = segs.length === 1 && page > 1 && page < numPages && prev === top && next === bottom;

    // Top edge: first page, or a page-break where a new section starts here (only
    // "(début)"; that section's "(fin)" sits at the bottom of the previous page),
    // or — for an interior page — the carried-over section.
    if (page === 1 || (prev && prev !== top)) {
      out.push({ key: `${m.id}-top`, y: 0, color: m.color, side, below: `${top} (${debut})` });
    } else if (interior) {
      out.push({ key: `${m.id}-top`, y: 0, color: m.color, side, below: `${top} (${enCours})` });
    }

    // Mid-page boundaries: one line between each pair of consecutive segments.
    for (let s = 1; s < segs.length; s++) {
      out.push({
        key: `${m.id}-seg${s}`,
        y: segs[s].start,
        color: m.color,
        side,
        above: `${segs[s - 1].category} (${fin})`,
        below: `${segs[s].category} (${debut})`,
      });
    }

    // Bottom edge: the last page, a page-break where the next page starts a
    // different section (its "(fin)" shows here), or — for an interior page — the
    // carried-over section.
    if (page === numPages || (next && next !== bottom)) {
      out.push({ key: `${m.id}-bottom`, y: 1, color: m.color, side, above: `${bottom} (${fin})` });
    } else if (interior) {
      out.push({ key: `${m.id}-bottom`, y: 1, color: m.color, side, above: `${bottom} (${enCours})` });
    }

    // Nudge each model's lines by a pixel so two boundaries at the same height
    // don't overlap and hide one another.
    return out.map((o) => ({ ...o, offset: i }));
  });

  // The overlay (dashed lines + labels) drawn over a page canvas. Labels sit
  // OUTSIDE the image — first model to the left, second to the right — so they
  // never hide page content. Shared by the inline viewer and the zoom modal.
  const boundaryOverlay = boundaryLines.map((ln) => {
    const sideCls = ln.side === 'left' ? 'right-full mr-2' : 'left-full ml-2';
    const labelCls = `absolute z-10 ${sideCls} whitespace-nowrap rounded px-1 text-[12px] font-semibold text-white`;
    return (
      <div
        key={ln.key}
        className="pointer-events-none absolute inset-x-0"
        style={{
          top: `${Math.min(Math.max(ln.y, 0.01), 0.99) * 100}%`,
          borderTop: `2px dashed ${ln.color}`,
          transform: `translateY(${ln.offset}px)`,
        }}
      >
        {ln.above && (
          <span className={`${labelCls} bottom-0.5`} style={{ backgroundColor: ln.color }}>
            {ln.above}
          </span>
        )}
        {ln.below && (
          <span className={`${labelCls} top-0.5`} style={{ backgroundColor: ln.color }}>
            {ln.below}
          </span>
        )}
      </div>
    );
  });

  // THE ZOOM'S CARD, around the page (see the zoom below). Its gutters are where the
  // section labels hang, so a side holding labels gets room for them and a side without
  // keeps the plain margin — read off the lines actually DRAWN, not off which models ran:
  // the first model's labels go left and EVERY other's right, and a check of "the second
  // model" missed a third one's labels, which then ran off the card and scrolled it.
  const zoomLabelSide = (side: string) => !filter && boundaryLines.some((l) => l.side === side);
  const zoomGutter = {
    left: zoomLabelSide('left') ? ZOOM_LABEL_GUTTER : ZOOM_GUTTER,
    right: zoomLabelSide('right') ? ZOOM_LABEL_GUTTER : ZOOM_GUTTER,
  };
  // The HEIGHT the card leaves the page, MEASURED: the card is the window's full height and
  // the page's row is what its top row and page navigation leave. It was guessed at from
  // their sizes, the guess was short, and the card scrolled.
  const [zoomArea, setZoomArea] = useState<HTMLDivElement | null>(null);
  const [zoomH, setZoomH] = useState(0);
  useEffect(() => {
    if (!zoomArea) return;
    const ro = new ResizeObserver(() => setZoomH(zoomArea.clientHeight));
    ro.observe(zoomArea);
    return () => ro.disconnect();
  }, [zoomArea]);

  // When zoomed, render the current page as large as the card allows.
  useEffect(() => {
    if (!zoomed) return;
    const doc = docRef.current;
    const canvas = zoomCanvasRef.current;
    if (!doc || !canvas || zoomH <= 0) return;
    let cancelled = false;

    (async () => {
      try {
        const pdfPage = await doc.getPage(page);
        if (cancelled) return;
        const base = pdfPage.getViewport({ scale: 1 });
        const scale = Math.min(
          zoomH / base.height,
          (window.innerWidth - ZOOM_FRAME_W - zoomGutter.left - zoomGutter.right) / base.width,
        );
        const viewport = pdfPage.getViewport({ scale });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        zoomTaskRef.current?.cancel();
        const task = pdfPage.render({ canvas, canvasContext: ctx, viewport });
        zoomTaskRef.current = task;
        await task.promise;
      } catch {
        // Cancelled when closing or paging — ignore.
      }
    })();

    return () => {
      cancelled = true;
      zoomTaskRef.current?.cancel();
    };
  }, [zoomed, page, zoomH, zoomGutter.left, zoomGutter.right]);

  // In filter mode, black out the part of the page outside the wanted band, so
  // only the in-category section of a partial page shows. Same overlay used by
  // the inline viewer and the zoom modal.
  const band = filter?.bands[page];
  const blackoutOverlay = filter && band && (
    <>
      {band.top > 0 && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 rounded-t-lg bg-black"
          style={{ height: `${band.top * 100}%` }}
        />
      )}
      {band.bottom < 1 && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-lg bg-black"
          style={{ height: `${(1 - band.bottom) * 100}%` }}
        />
      )}
    </>
  );

  // Show the two-pass toggle whenever there are part boxes at all — even when the two
  // inferences match, or the first-pass box wasn't recorded (parts extracted before it
  // was tracked, where toggling just recolours the current boxes).
  const hasPartBoxes = !!partBoxes?.length;

  // Part boxes for THIS page, drawn as rectangles over the canvas with the full part
  // name on a tag ABOVE each box — never truncated (it wraps across rows as needed)
  // and never over the artwork. The current (refined) boxes are emerald; the toggled
  // first-pass boxes are amber, so which inference you're seeing is unmistakable. No
  // dimming (unlike BoxOverlay's single-hero emphasis) — several boxes share the page.
  const boxCls = showFirstPass ? 'border-amber-500' : 'border-blue-500';
  const tagCls = showFirstPass ? 'bg-amber-500' : 'bg-blue-500';
  const partBoxOverlay = partBoxes && boxPass !== 'off' && (
    <>
      {partBoxes
        .filter((b) => b.page === page)
        .map((b, i) => {
          const [x0, y0, x1, y1] = showFirstPass && b.firstBox ? b.firstBox : b.bbox;
          return (
            <div
              key={`${b.label}-${i}`}
              className={`pointer-events-none absolute rounded-sm border-2 ${boxCls}`}
              style={{
                left: `${x0 * 100}%`,
                top: `${y0 * 100}%`,
                width: `${(x1 - x0) * 100}%`,
                height: `${(y1 - y0) * 100}%`,
              }}
            >
              {b.label && (
                <span className={`absolute bottom-full left-1/2 mb-0.5 max-w-[10rem] -translate-x-1/2 whitespace-normal break-words rounded px-1 py-0.5 text-center text-[10px] font-semibold leading-tight text-white ${tagCls}`}>
                  {b.label}
                </span>
              )}
            </div>
          );
        })}
    </>
  );

  // Cycle the part boxes: current -> first pass -> none, shown whenever the page has any.
  // The button names the state it is IN, and its colour is that state's own — including
  // the dot, which goes hollow when nothing is drawn.
  const passToggle = hasPartBoxes ? (
    <button
      type="button"
      onClick={() => setBoxPass(NEXT_BOX_PASS[boxPass])}
      className={`pointer-events-auto inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold shadow-sm backdrop-blur transition-colors ${BOX_PASS_STYLE[boxPass].button}`}
    >
      <span className={`h-2 w-2 rounded-full ${BOX_PASS_STYLE[boxPass].dot}`} />
      {t(BOX_PASS_STYLE[boxPass].label)}
    </button>
  ) : null;

  // Navigation bounds + label, accounting for filter mode (step through the
  // matching pages, label as "Page N (i/total)").
  const nav = filter?.pages;
  const navIdx = nav ? nav.indexOf(page) : -1;
  const atStart = nav ? navIdx <= 0 : page <= 1;
  const atEnd = nav ? navIdx >= nav.length - 1 : page >= numPages;
  const navLabel =
    nav && navIdx >= 0 ? `${t('notice.pdf.page')} ${page} (${navIdx + 1}/${nav.length})` : undefined;

  return (
    <div className="relative flex h-full flex-col">
      {/* Two-pass box toggle, floated top-left (the parent's close button is top-right). */}
      {passToggle && <div className="absolute left-3 top-3 z-20">{passToggle}</div>}
      {/* Page canvas */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-slate-50 px-4 pb-1 pt-12">
        {loading && (
          <div className="flex h-full items-center justify-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {error && <p className="py-12 text-center text-sm text-red-600">{error}</p>}
        {!error && (
          <div className={`relative mx-auto w-fit ${loading ? 'hidden' : ''}`}>
            <canvas
              ref={canvasRef}
              onClick={() => !loading && numPages > 0 && setZoomed(true)}
              title={t('notice.pdf.zoom')}
              className="block max-w-full cursor-zoom-in rounded-lg shadow-lg"
            />
            {/* Dashed section-boundary lines + labels (hidden in filter mode). */}
            {!loading && !filter && boundaryOverlay}
            {/* Black out the off-category part of a partial page (filter mode). */}
            {!loading && blackoutOverlay}
            {/* Part bounding boxes over the manual (Phase 2). */}
            {!loading && partBoxOverlay}
          </div>
        )}
      </div>

      {/* Footer: page navigation, right under the page — no rule between them, it is the
          page's own control. */}
      <div className="px-4 pb-2">
        <PageNav page={page} numPages={numPages} onGo={go} disabled={loading} label={navLabel} atStart={atStart} atEnd={atEnd} />
      </div>

      {/* THE ZOOM: the page in a card, like every dialog (see modalCardCls) — its section
          labels, the pass toggle, the close button and the page navigation all inside it.
          They floated on the black around a bare page, and the labels beside it had no
          ground at all. Click the backdrop (or Escape) to close. */}
      {zoomed && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setZoomed(false)}
          className={`${wideOverlayCls} ${modalBackdropCls}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            // THE WINDOW'S HEIGHT, and as wide as the page and its gutters: the page is sized
            // to fit it (see zoomH), so there is never anything to scroll.
            className={`relative flex h-full max-w-full flex-col items-center overflow-hidden pb-3 pt-12 ${modalCardCls}`}
            style={{ paddingLeft: zoomGutter.left, paddingRight: zoomGutter.right }}
          >
            {passToggle && <div className="absolute left-3 top-3 z-10">{passToggle}</div>}
            <button
              type="button"
              onClick={() => setZoomed(false)}
              aria-label={t('notice.pdf.close')}
              className={modalCloseCls}
            >
              <X className="h-5 w-5" />
            </button>
            <div ref={setZoomArea} className="flex min-h-0 flex-1 items-center justify-center">
              <div className="relative">
                <canvas ref={zoomCanvasRef} className="block rounded-lg shadow-lg" />
                {!filter && boundaryOverlay}
                {blackoutOverlay}
                {partBoxOverlay}
              </div>
            </div>
            <div className="mt-3">
              <PageNav page={page} numPages={numPages} onGo={go} disabled={loading} label={navLabel} atStart={atStart} atEnd={atEnd} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Previous / "Page X of Y" / next control, shared by the toolbar and the zoom
// modal so both stay in sync.
function PageNav({
  page,
  numPages,
  onGo,
  disabled = false,
  label,
  atStart,
  atEnd,
}: {
  page: number;
  numPages: number;
  onGo: (delta: number) => void;
  disabled?: boolean;
  label?: string;
  atStart?: boolean;
  atEnd?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex items-center justify-center gap-4">
      <button
        type="button"
        onClick={() => onGo(-1)}
        disabled={disabled || (atStart ?? page <= 1)}
        className="rounded-lg border border-slate-300 bg-white p-2 text-slate-700 transition-colors hover:border-blue-500 hover:bg-blue-50 disabled:opacity-40"
        aria-label={t('notice.pdf.prev')}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="min-w-[6rem] text-center text-sm text-slate-600">
        {label ?? (numPages > 0 ? `${t('notice.pdf.page')} ${page} ${t('notice.pdf.of')} ${numPages}` : '—')}
      </span>
      <button
        type="button"
        onClick={() => onGo(1)}
        disabled={disabled || (atEnd ?? page >= numPages)}
        className="rounded-lg border border-slate-300 bg-white p-2 text-slate-700 transition-colors hover:border-blue-500 hover:bg-blue-50 disabled:opacity-40"
        aria-label={t('notice.pdf.next')}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
